// src/components/common/Toast.tsx

import { useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

interface ToastProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ type, message, onClose, duration = 5000 }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const configs = {
    success: {
      icon: CheckCircle,
      bgClass: 'bg-green-500/10 border-green-500/50',
      iconClass: 'text-green-500',
      textClass: 'text-green-400'
    },
    error: {
      icon: XCircle,
      bgClass: 'bg-red-500/10 border-red-500/50',
      iconClass: 'text-red-500',
      textClass: 'text-red-400'
    },
    info: {
      icon: Info,
      bgClass: 'bg-blue-500/10 border-blue-500/50',
      iconClass: 'text-blue-500',
      textClass: 'text-blue-400'
    },
    warning: {
      icon: AlertTriangle,
      bgClass: 'bg-orange-500/10 border-orange-500/50',
      iconClass: 'text-orange-500',
      textClass: 'text-orange-400'
    }
  };

  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border ${config.bgClass} backdrop-blur-sm animate-in slide-in-from-top-2 duration-300 max-w-md shadow-lg`}>
      <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconClass}`} />
      <p className={`flex-1 ${config.textClass} font-medium`}>{message}</p>
      <button
        onClick={onClose}
        className={`${config.textClass} hover:opacity-70 transition-opacity`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}